import { useState } from "react";
import { toast } from "sonner";
import { Plus, History, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FormDialog } from "@/components/FormDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { money, useList } from "@/lib/technicians";

const scope = (r, meta, zones) => [
  r.service_type_code ? meta?.service_types?.find((s) => s.code === r.service_type_code)?.name || r.service_type_code : null,
  r.business_unit_code ? meta?.business_units?.find((b) => b.code === r.business_unit_code)?.name || r.business_unit_code : null,
  r.zone_id ? zones.find((z) => z.id === r.zone_id)?.path || "Zona" : null,
].filter(Boolean).join(" · ") || "Tarifa general";

export const RatesPanel = ({ techId, zones }) => {
  const { meta, can } = useAuth();
  const [rates, reload] = useList(`/technicians/${techId}/rates`);
  const [dlg, setDlg] = useState(null);
  const opts = (arr, k = "code", l = "name") => (arr || []).map((x) => ({ value: x[k], label: x[l] }));
  const newFields = [
    { name: "service_type_code", label: "Tipo de servicio", type: "select", nullable: true, noneLabel: "Cualquiera", options: opts(meta?.service_types) },
    { name: "business_unit_code", label: "Unidad de negocio", type: "select", nullable: true, noneLabel: "Cualquiera", options: opts(meta?.business_units) },
    { name: "zone_id", label: "Zona", type: "select", nullable: true, noneLabel: "Cualquiera", options: zones.map((z) => ({ value: z.id, label: z.path })) },
    { name: "amount", label: "Monto", type: "number", required: true },
    { name: "currency", label: "Moneda", default: "MXN" },
    { name: "valid_from", label: "Vigente desde", type: "date", required: true },
    { name: "valid_to", label: "Vigente hasta (opcional)", type: "date" },
    { name: "notes", label: "Notas" },
  ];
  const versionFields = newFields.filter((x) => ["amount", "currency", "valid_from", "valid_to", "notes"].includes(x.name));
  const save = async (form) => {
    const body = { ...form, valid_to: form.valid_to || null };
    if (dlg.rate) await api.post(`/technician-rates/${dlg.rate.id}/new-version`, body);
    else await api.post(`/technicians/${techId}/rates`, body);
    toast.success(dlg.rate ? "Nueva versión de tarifa creada" : "Tarifa creada");
    reload();
  };
  const deactivate = async (r) => {
    try { await api.post(`/technician-rates/${r.id}/deactivate`); reload(); toast.success("Tarifa desactivada"); } catch (e) { toast.error(errMsg(e)); }
  };
  const editable = can("rates.write");
  return (
    <div className="space-y-3" data-testid="tech-rates-panel">
      <p className="text-xs text-muted-foreground">Dato interno. No se muestra al técnico ni en el PDF. Las tarifas no se editan: se crea una nueva versión.</p>
      {editable && <Button size="sm" onClick={() => setDlg({})} data-testid="rate-new-button"><Plus className="mr-1 h-3.5 w-3.5" />Nueva tarifa</Button>}
      {rates.map((r) => (
        <div key={r.id} className={`rounded-lg border border-border px-3 py-2 text-sm ${r.active ? "" : "opacity-50"}`} data-testid={`rate-row-${r.id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono font-semibold">{money(r.amount, r.currency)}</span>
            <span className="text-muted-foreground">{scope(r, meta, zones)}</span>
            <span className="font-mono text-[10px] text-muted-foreground">v{r.version}</span>
            {!r.active && <span className="text-xs text-red-400">Inactiva</span>}
            {editable && r.active && <div className="ml-auto flex gap-1">
              <Button size="sm" variant="ghost" onClick={() => setDlg({ rate: r })} data-testid={`rate-version-${r.id}`}><History className="mr-1 h-3.5 w-3.5" />Nueva versión</Button>
              <Button size="sm" variant="ghost" onClick={() => deactivate(r)} data-testid={`rate-deactivate-${r.id}`}><Ban className="h-3.5 w-3.5" /></Button>
            </div>}
          </div>
          <div className="text-xs text-muted-foreground">Vigencia: {fmtDate(r.valid_from)} → {r.valid_to ? fmtDate(r.valid_to) : "sin fecha final"}</div>
        </div>
      ))}
      {!rates.length && <p className="text-sm text-muted-foreground">Sin tarifas configuradas.</p>}
      <FormDialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)} title={dlg?.rate ? "Nueva versión de tarifa" : "Nueva tarifa"} fields={dlg?.rate ? versionFields : newFields}
        initial={dlg?.rate ? { currency: dlg.rate.currency } : null} onSubmit={save} testid="rate-form" />
    </div>
  );
};
